// src/pages/OverviewPage.tsx / JobDetailsPage.tsx
import React, { useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { STATUS_CONFIG } from 'components/JobsTable/JobsDetailsSidebar/JobDetailsSidebarHelpers';
import { useGetJobDetail } from 'hooks/jobs';
import { JobPropertiesContent } from 'components/JobsTable/JobsDetailsSidebar/Modals/Contents/JobPropertiesContent';
import { JobCodeContent } from 'components/JobsTable/JobsDetailsSidebar/Modals/Contents/JobCodeContent';
import { JobMetadataOverview } from 'components/reusables/JobMetadataOverview/JobMetadataOverview';
import './JobDetailsPage.scss';

type PanelTab = 'properties' | 'code';

export function JobDetailsPage() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id') ?? '';
  const prefix = searchParams.get('prefix') ?? '';
  const expires = searchParams.get('expires') ?? '';
  const signature = searchParams.get('signature') ?? '';
  const schemaName = searchParams.get('schema') ?? '';
  const { clusterName } = useParams<{ clusterName: string }>();

  const isSignedURL = !!expires && !!signature;
  const [activeTab, setActiveTab] = useState<PanelTab>('properties');

  const { data: jobDetails, isLoading } = useGetJobDetail({
    jobId,
    schemaName,
    isEnabled: !!jobId,
    isSignedURL,
    expires,
    signature,
    prefix,
    clusterName,
  });

  const statusCfg = jobDetails
    ? STATUS_CONFIG[jobDetails.sparkJobStatus as unknown as JobStatus]
    : null;

  return (
    <div className="job-details-page">
      <div className="job-details-page__left">
        <div className="job-details-page__header">
          <div className="job-details-page__header-meta">
            <span className="job-details-page__eyebrow">Job Details</span>
            {isLoading ? (
              <Skeleton.Input
                active
                size="small"
                className="job-details-page__skeleton-title"
              />
            ) : (
              <h1 className="job-details-page__title">
                {jobDetails?.sparkJobId ?? jobId ?? '—'}
              </h1>
            )}
          </div>
          {!isLoading && statusCfg && (
            <span
              className={`job-details-page__status-pill ${statusCfg.className}`}>
              {statusCfg.icon}
              <span className="job-details-page__status-text">
                {statusCfg.label}
              </span>
            </span>
          )}
        </div>

        <div className="job-details-page__sections">
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 12 }} />
          ) : jobDetails ? (
            <JobMetadataOverview
              jobDetails={jobDetails}
              showArcaneMetrics={false}
            />
          ) : (
            <div className="job-details-page__empty">
              No job data available.
            </div>
          )}
        </div>
      </div>

      <div className="job-details-page__right">
        <div className="job-details-page__tab-group" role="tablist">
          {/* Managed Tab Items cleanly using class lists */}
          {['properties', 'code'].map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              className={`job-details-page__tab ${activeTab === tab ? 'job-details-page__tab--active' : ''}`}
              onClick={() => setActiveTab(tab as PanelTab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="job-details-page__panel-content">
          {activeTab === 'properties' ? (
            <JobPropertiesContent
              job={jobDetails}
              isLoading={isLoading}
              height="915px"
            />
          ) : (
            <div className="job-details-page__code-panel-container">
              <JobCodeContent job={jobDetails} isLoading={isLoading} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JobDetailsPage;
